import { createContext } from 'react';

import { UploadImageFunc } from '@api/services/imageManager';
import { ImageHeaders } from '@hooks/imageHooks/useImageHeaders';
import { ImageType, PromoImageType } from '@models/Image';

export type ImageMetadataMap = Partial<Record<ImageType, ImageHeaders | undefined>>;

export const ImageContexts = ['uploaded', 'default'] as const;
export type ImageContext = (typeof ImageContexts)[number];

export type ImageGroupDataValues = {
  /**
   * Contextual images (user uploaded)
   */
  contextImages: ImageMetadataMap;
  defaultImageAspectRatio?: PromoImageType;
  defaultImageMissing: boolean;
  /**
   * Default images (from provider)
   */
  defaultImages: ImageMetadataMap;
  defaultImageType?: ImageType;
  deleteImage?: (imageType: ImageType) => void;
  disableDelete: boolean;
  disabledImageTypes: ImageType[];
  /**
   * Contextual images (user uploaded)
   */
  fallbackImages: ImageMetadataMap;
  /**
   * Filename base for use when downloading
   */
  filenameBase: string;
  handleUploadImage?: UploadImageFunc;
  hasContextImages: boolean;
  hasDefaultImages: boolean;
  /**
   * Combined image map, prioritized as such:
   * 1. Contextual images
   * 2. Default images
   * 3. Fallback images
   */
  imageMap: ImageMetadataMap;
  isDeleting: (imageType: ImageType) => boolean;
  loading: boolean;
  refreshImages: (imageType?: ImageType | ImageType[]) => void;
};

const ImageGroupDataContext = createContext<ImageGroupDataValues>({
  contextImages: {},
  defaultImageMissing: false,
  defaultImages: {},
  disableDelete: false,
  disabledImageTypes: [],
  fallbackImages: {},
  filenameBase: '',
  hasContextImages: false,
  hasDefaultImages: false,
  imageMap: {},
  isDeleting: () => false,
  loading: false,
  refreshImages: () => void 0,
});

export default ImageGroupDataContext;
